import { Form, TextField, useForm } from '@/components/form';
import { Sidebar, SidebarHeader, toggle, useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import {
  Blanket,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  FormattedRelativeTime,
  Modal,
  ModalBody,
  ModalFooter,
  PlainButton,
  Scrollable,
  Spinner,
  Text,
  Truncated,
  useConfirmationModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { useState } from 'react';
import { z } from 'zod';
import { useCopilotSessionContext } from './components/session-provider';
import { MAX_COPILOT_SESSION_NAME_LENGTH, useCopilotActions, useCopilotHistory } from './hooks';
import { useIsFullScreenAssistantPage } from './index';
import * as styles from './index.css';
import { isEmptyString } from '@/utils/string';

export function History() {
  const isActive = useSidebarActive('copilot-history');
  const { track } = useAnalytics();
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();

  return (
    <Sidebar
      id="copilot-history"
      onToggle={(isOpen) => {
        if (isOpen) {
          track('aiAssistant', 'viewedHistory', {});
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <SidebarHeader
          title="History"
          leadingActions={
            !isFullScreenAssistantPage ? (
              <Button
                variant="subtle"
                size="sm"
                icon="chevron-left"
                isIconOnly={true}
                onClick={() => {
                  toggle('copilot');
                }}
              />
            ) : undefined
          }
          onClose={() => {
            toggle('copilot-history');
          }}
        />

        {isActive ? <Chats /> : null}
      </Box>
    </Sidebar>
  );
}

function Chats() {
  const currentSession = useCopilotSessionContext();
  const history = useCopilotHistory();
  const { track } = useAnalytics();
  const actions = useCopilotActions();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();

  function continueSession(id: number) {
    track('aiAssistant', 'historyItemClicked', {});
    currentSession.reset().initialize(`${id}`);
    if (isFullScreenAssistantPage) {
      toggle('copilot-history');
    } else {
      toggle('copilot');
    }
  }

  function deleteSession(id: number) {
    confirm({
      title: 'Delete Chat',
      message: 'Are you sure you want to delete this chat? This action cannot be undone.',
      isDanger: true,
      async onConfirm() {
        try {
          track('aiAssistant', 'historyItemDeleted', {});
          await actions.deleteSession(id);

          if (currentSession.id === `${id}`) {
            currentSession.reset();
          }
        } catch (error) {
          sentryCaptureException(error);
          snackbars.create({
            variant: 'error',
            message: 'Failed to delete chat history. Please try again.',
          });
        }
      },
    });
  }

  async function renameSession(id: number, name: string) {
    try {
      track('aiAssistant', 'historyItemRenamed', {});
      await actions.renameSession(id, name);

      if (currentSession.id === `${id}`) {
        currentSession.setSessionMeta({
          ...currentSession.__meta.get(),
          name,
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      snackbars.create({
        variant: 'error',
        message: 'Failed to rename chat history. Please try again.',
      });
    }
  }

  const totalItems = history.data?.pages?.[0]?.body?.meta?.pagination?.total ?? -1;

  return (
    <Box flexGrow="1" h="0" display="flex" flexDirection="column" position="relative">
      {history.isError === true ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="error">Failed to load history</EmptyStateTitle>
            <EmptyStateDescription>
              There was an error loading your chat history. Please try again.
            </EmptyStateDescription>
          </EmptyState>
        </Box>
      ) : history.isLoading || history.data == null ? (
        <Blanket>
          <Spinner />
        </Blanket>
      ) : totalItems === 0 ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="chat">No chats yet</EmptyStateTitle>
            <EmptyStateDescription>Start a chat with the AI assistant</EmptyStateDescription>
            <EmptyStateActions>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  toggle('copilot');
                  currentSession.reset();
                }}
              >
                Start Chat
              </Button>
            </EmptyStateActions>
          </EmptyState>
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" p="200">
            <Text color="subtle" size="xs">
              {totalItems} {plur('chat', totalItems)}
            </Text>
            <Text color="subtle" size="xs">
              Stored for 30 days
            </Text>
          </Box>
          <Scrollable
            pt="0"
            p="200"
            gap="100"
            display="flex"
            flexDirection="column"
            overflow="y"
            h="full"
            onTouchEdge={(edge) => {
              if (edge === 'bottom' && history.isFetching === false) {
                history.fetchNextPage();
              }
            }}
          >
            {history.data.pages
              .flatMap((page) => page.body.sessions)
              .map((session) => (
                <HistoryItem
                  key={session.id}
                  id={session.id}
                  onClick={async () => {
                    continueSession(session.id);
                  }}
                  onRename={(name) => renameSession(session.id, name)}
                  onDelete={() => deleteSession(session.id)}
                  name={session.name}
                  createdAt={session.created_at}
                />
              ))}

            {history.isFetchingNextPage === true ? (
              <Box display="flex" justifyContent="center" p="100">
                <Spinner />
              </Box>
            ) : null}
          </Scrollable>
        </>
      )}
    </Box>
  );
}

function HistoryItem({
  onClick,
  onRename,
  onDelete,
  id,
  name,
  createdAt,
}: {
  onClick: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => void;
  id: number;
  name: string | null;
  createdAt: number;
}) {
  const [isRenaming, setIsRenaming] = useState(false);

  return (
    <Box position="relative" className={styles.historyItem}>
      <PlainButton onClick={onClick} display="block" w="full" textAlign="left">
        {(context) => (
          <>
            <Box as="span" className={context.isLoading === true ? styles.historyItemLoading : undefined}>
              <Text weight="medium" display="block" mr="400">
                <Truncated>{!isEmptyString(name) ? name : `Chat ${id}`}</Truncated>
              </Text>
              <Text color="subtle">
                Started <FormattedRelativeTime date={createdAt * 1000} cursor="pointer" shouldUppercaseFirst={false} />
              </Text>
            </Box>
            {context.isLoading === true ? (
              <Blanket>
                <Spinner />
              </Blanket>
            ) : null}
          </>
        )}
      </PlainButton>
      <DropdownMenu>
        <DropdownMenuTriggerButton
          className={styles.dropdownMenuTopRight}
          isIconOnly={true}
          icon="more"
          size="sm"
          variant="subtle"
        />
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => {
              setIsRenaming(true);
            }}
          >
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem isDanger={true} onClick={() => onDelete()}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <RenameModal
        isOpen={isRenaming}
        name={name ?? ''}
        onClose={() => setIsRenaming(false)}
        onSave={async (newName) => {
          await onRename(newName);
          setIsRenaming(false);
        }}
      />
    </Box>
  );
}

function RenameModal({
  isOpen,
  name,
  onSave,
  onClose,
}: {
  isOpen: boolean;
  name: string;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const form = useForm({
    defaultValues: {
      name,
    },
    validators: {
      onChange: z.object({
        name: z
          .string()
          .min(1, 'Chat name is required')
          .max(
            MAX_COPILOT_SESSION_NAME_LENGTH,
            `Chat name must be at most ${MAX_COPILOT_SESSION_NAME_LENGTH} characters`,
          ),
      }),
    },
    async onSubmit(props) {
      await onSave(props.value.name);
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" title="Rename Chat">
      <ModalBody>
        <Form api={form} id="rename-item">
          <TextField
            api={form}
            name="name"
            label="Chat Name"
            placeholder="Enter a name for this chat"
            defaultValue={name}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" form="rename-item" isLoading={form.state.isSubmitting}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}
